<script setup>
import { post } from "@/apis";
import { formatSecond } from "@/utils";
import { watchEffect, ref, nextTick } from "vue";
import Disc from "@/assets/disc.png";
import Clock from "@/assets/clock.png";
import IconList from "@/assets/icon-list.png";
import IconBookLock from "@/assets/icon-book-lock.png";
import RemarkDialog from "./RemarkDialog.vue";
import Empty from "@/components/Empty.vue";
import { openApp } from "@/utils/appLink";
import CustomDialog from "../../../components/CustomDialog.vue";
import { OSSImageCompress } from "@/utils/index.js";
import VideoCover from "@/assets/video-cover.png";

const { rank } = defineProps(["rank"]);

const album = ref({});
const showMore = ref(false);
const remarkParentRef = ref();
const remarkRef = ref();
const dialogShow = ref(false);
const showCustomDialog = ref(false);

const getAlbum = async () => {
  const res = await post(
    "/api/classpal/app/v1/h5/api/albumElementsBusinessRef/app/getElementsBusinessRefMaterialRef",
    {
      businessId: rank.id,
      businessType: 27,
    }
  );
  album.value = {
    ...res.data,
    albumRemark: res.data.albumRemark,
  };
  await nextTick();
  if (remarkRef.value.offsetWidth > remarkParentRef.value.offsetWidth) {
    showMore.value = true;
  }
};

watchEffect(() => {
  if (rank.id) {
    getAlbum();
  }
});

const handleCheckMore = () => {
  dialogShow.value = true;
};

const handleToApp = (item) => {
  showCustomDialog.value = true;
};

const onConfirm = () => {
  openApp();
};
</script>

<template>
  <div v-if="!album.id" style="width: 100%; height: 100%">
    <Empty />
  </div>
  <div class="album-container" v-else>
    <div class="album">
      <div class="info">
        <van-image class="info-img" :src="album.albumCover || VideoCover" />
        <van-image class="info-img-disc" :src="Disc" />
        <div class="info-desc">
          <div class="info-desc-title truncate">{{ album.albumTitle }}</div>
          <div class="info-desc-time">
            <van-image class="info-desc-time-icon" :src="Clock" />
            <span>{{ formatSecond(album.materialDurations) }}</span>
          </div>
          <div class="info-desc-text" ref="remarkParentRef">
            <span :class="showMore ? 'truncate' : ''" ref="remarkRef">
              {{ album.albumRemark }}
            </span>
            <span
              class="info-desc-text-more"
              v-if="showMore"
              @click.stop="handleCheckMore"
            >
              更多
            </span>
          </div>
        </div>
      </div>

      <div class="list-top">
        <van-image class="list-top-icon" :src="IconList" />
        <span>播放列表</span>
        <span class="list-top-num">({{ album.refBusinessList?.length }})</span>
      </div>

      <div class="list">
        <div
          v-for="(material, index) in album.refBusinessList"
          :key="material.id"
          class="material"
          @click="handleToApp(item)"
        >
          <div class="material-serial">{{ index + 1 }}</div>
          <div class="material-info-cover-box">
            <van-image
              :src="
                OSSImageCompress(material.cover, {
                  height: '400',
                  scaleType: 'lfit',
                })
              "
              class="material-info-cover"
            />
            <van-image
              v-if="material.isLock"
              :src="IconBookLock"
              class="material-lock"
            />
          </div>
          <div class="material-info">
            <div class="material-info-title truncate">
              {{ material.businessName }}
            </div>
            <div class="material-info-time">
              <van-image :src="Clock" class="material-info-time-icon" />
              <span>{{ formatSecond(material.materialDuration) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <RemarkDialog v-model="dialogShow" :remark="album.albumRemark" />

  <CustomDialog
    v-model="showCustomDialog"
    title="视频课程"
    content="视频课程"
    @confirm="onConfirm"
  />
</template>

<style lang="scss" scoped>
.album-container {
  width: 100%;
  height: 100%;
  display: flex;
}
.album {
  min-height: 0;
  flex: 1;
  background-color: #fff;
  margin-top: 12px;
  overflow-y: auto;
  padding: 16px;
}
.info {
  width: 100%;
  height: 66px;
  display: flex;
  align-items: center;
  position: relative;
  .info-img-disc {
    width: 66px;
    height: 100%;
    margin-left: 8px;
  }
  .info-img {
    width: 66px;
    height: 100%;
    border-radius: 12px;
    overflow: hidden;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }
  .info-desc {
    height: 100%;
    flex: 1;
    min-width: 0;
    margin-left: 16px;
    font-size: 16px;
    color: #000000d9;
  }
  .info-desc-title {
    font-weight: 600;
    line-height: 24px;
  }
  .info-desc-time {
    font-size: 12px;
    color: #00000059;
    height: 20px;
    font-weight: 400;
    display: flex;
    align-items: center;
    .info-desc-time-icon {
      width: 14px;
      height: 14px;
      margin-right: 4px;
    }
  }
  .info-desc-text {
    line-height: 22px;
    font-size: 14px;
    color: #00000059;
    font-weight: 400;
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  .info-desc-text-more {
    margin-left: 4px;
    flex-shrink: 0;
  }
}
.list-top {
  margin-top: 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #000000d9;
  line-height: 22px;
  .list-top-icon {
    width: 20px;
    height: 20px;
    margin-right: 2px;
  }
  .list-top-num {
    font-size: 12px;
    color: #00000059;
    margin-left: 4px;
  }
}

.list {
  margin-top: 12px;
  font-size: 16px;
  .material {
    &:not(:last-child) {
      margin-bottom: 16px;
    }
    display: flex;
    align-items: center;
    font-size: 16px;
  }
  .material-serial {
    color: #00000059;
    margin-right: 16px;
  }
  .material-info-cover-box {
    width: 88px;
    height: 66px;
    border-radius: 8px;
    margin-right: 16px;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
  }
  .material-info-cover {
    width: 88px;
    height: 66px;
    border-radius: 8px;
    margin-right: 16px;
    flex-shrink: 0;
  }
  .material-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    .material-info-title {
      color: #000000d9;
      line-height: 24px;
    }
    .material-info-time {
      display: flex;
      align-items: center;
      font-size: 12px;
      color: #00000059;
    }
    .material-info-time-icon {
      width: 14px;
      height: 14px;
      margin-right: 4px;
    }
  }
  .material-lock {
    width: 24px;
    height: 24px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
